import {
  enrollmentResultSchema,
  loginResponseSchema,
  totpSetupResponseSchema,
  type AuthenticatedResponse,
  type LoginResponse,
} from '@growup/accounts-shared';
import { Button, Card, Field, Input, Logo, Notice, PasswordInput, Theme } from '@growup/ui';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { messageOf, post } from './api';

type Step =
  | { kind: 'credentials' }
  | { kind: 'code'; challengeToken: string; recovery: boolean }
  | { kind: 'enroll'; challengeToken: string; secret: string; otpauthUrl: string }
  | { kind: 'recovery-codes'; codes: string[]; session: AuthenticatedResponse };

/**
 * Staff sign-in with the central account (identity-api). Staff must use a
 * second factor (RT-G4): an authenticator app, set up here on the first
 * sign-in. Passkeys are accepted by identity-api and come to this screen later.
 */
export function SignIn({ onSignedIn }: { onSignedIn: (session: AuthenticatedResponse) => void }) {
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const afterLogin = async (result: LoginResponse) => {
    setCode('');
    if (result.status === 'authenticated') {
      onSignedIn(result);
    } else if (result.status === 'mfa_required') {
      setStep({ kind: 'code', challengeToken: result.challengeToken, recovery: false });
    } else {
      const setup = await post(
        '/api/identity/mfa/totp/setup',
        { challengeToken: result.challengeToken },
        totpSetupResponseSchema,
      );
      setStep({ kind: 'enroll', challengeToken: result.challengeToken, ...setup });
    }
  };

  const submitCredentials = () =>
    run(async () =>
      afterLogin(
        await post('/api/identity/sessions', { identifier, password }, loginResponseSchema),
      ),
    );

  const submitCode = (challengeToken: string, recovery: boolean) =>
    run(async () =>
      afterLogin(
        await post(
          recovery ? '/api/identity/sessions/mfa/recovery' : '/api/identity/sessions/mfa/totp',
          { challengeToken, code: code.trim() },
          loginResponseSchema,
        ),
      ),
    );

  const confirmEnrollment = (challengeToken: string) =>
    run(async () => {
      const result = await post(
        '/api/identity/mfa/totp/confirm',
        { challengeToken, code: code.trim() },
        enrollmentResultSchema,
      );
      if (!result.session) throw new Error('A configuração não devolveu sessão. Entre de novo.');
      setStep({
        kind: 'recovery-codes',
        codes: result.recoveryCodes ?? [],
        session: result.session,
      });
    });

  return (
    <Theme name="admin" className="flex min-h-screen items-center justify-center px-4">
      <main className="flex w-full max-w-sm flex-col gap-6">
        <h1 className="flex items-center gap-3">
          <Logo height={20} />
          <span className="font-sub text-label font-bold uppercase text-gu-ink-muted">Gestão</span>
        </h1>
        {step.kind === 'credentials' ? (
          <Card headingLevel={2} title="Entrar">
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCredentials();
              }}
            >
              <Field label="Login ou email">
                <Input
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                />
              </Field>
              <Field label="Palavra-passe">
                <PasswordInput
                  toggleLabel="Mostrar palavra-passe"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              {error ? <Notice tone="danger">{error}</Notice> : null}
              <Button type="submit" disabled={busy}>
                Entrar
              </Button>
            </form>
          </Card>
        ) : null}

        {step.kind === 'code' ? (
          <Card headingLevel={2} title="Segundo passo">
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCode(step.challengeToken, step.recovery);
              }}
            >
              <Field
                label={step.recovery ? 'Código de recuperação' : 'Código da app autenticadora'}
              >
                <Input
                  autoComplete="one-time-code"
                  inputMode={step.recovery ? 'text' : 'numeric'}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
              {error ? <Notice tone="danger">{error}</Notice> : null}
              <Button type="submit" disabled={busy}>
                Confirmar
              </Button>
              <button
                type="button"
                className="text-left text-body text-gu-ink-muted underline-offset-4 hover:text-gu-ink hover:underline"
                onClick={() => {
                  setCode('');
                  setStep({ ...step, recovery: !step.recovery });
                }}
              >
                {step.recovery
                  ? 'Usar a app autenticadora'
                  : 'Não tenho o telemóvel: usar um código de recuperação'}
              </button>
            </form>
          </Card>
        ) : null}

        {step.kind === 'enroll' ? (
          <Card headingLevel={2} title="Ativar o segundo passo">
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void confirmEnrollment(step.challengeToken);
              }}
            >
              <p className="text-body text-gu-ink-muted">
                O acesso à gestão exige uma app autenticadora (Google Authenticator, Microsoft
                Authenticator, 1Password…). Leia o código com a app e escreva os 6 dígitos que ela
                mostra.
              </p>
              <OtpQr url={step.otpauthUrl} />
              <p className="break-all text-center font-mono text-caption text-gu-ink-muted">
                {step.secret}
              </p>
              <Field label="Código de 6 dígitos">
                <Input
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
              {error ? <Notice tone="danger">{error}</Notice> : null}
              <Button type="submit" disabled={busy}>
                Ativar e entrar
              </Button>
            </form>
          </Card>
        ) : null}

        {step.kind === 'recovery-codes' ? (
          <Card headingLevel={2} title="Códigos de recuperação">
            <div className="flex flex-col gap-3">
              <p className="text-body text-gu-ink-muted">
                Guarde estes códigos num sítio seguro. Cada um serve uma vez para entrar se perder o
                telemóvel. Não voltam a ser mostrados.
              </p>
              <ul className="grid grid-cols-2 gap-1 font-mono text-body">
                {step.codes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              <Button onClick={() => onSignedIn(step.session)}>Já guardei, continuar</Button>
            </div>
          </Card>
        ) : null}
      </main>
    </Theme>
  );
}

function OtpQr({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(url, { margin: 1, width: 220 })
      .then((data) => {
        if (live) setSrc(data);
      })
      .catch(() => setSrc(null));
    return () => {
      live = false;
    };
  }, [url]);
  return src ? (
    <img src={src} alt="Código QR para a app autenticadora" className="mx-auto bg-white p-2" />
  ) : null;
}
